/** Pools de QR fixos por tipo de visita (paridade com thalamus-acesso-mobile). */

export const TIPO_QR_VISITANTE = "visitante";
export const TIPO_QR_PRESTADOR = "prestador";

const LIMITE_VISITANTE = 5;
const LIMITE_PRESTADOR = 10;

const RE_PRESTADOR = /prestador(?:\s+de\s+servi[cç]o)?\s*[-#:.\s]*(\d+)/i;
const RE_VISITANTE = /visitantes?\s*[-#:.\s]*(\d+)/i;
const RE_ID_VISITANTE = /^visitante-(\d+)$/i;
const RE_ID_PRESTADOR = /^prestador-(\d+)$/i;
const RE_CODIGO_V = /^V0*(\d+)$/i;
const RE_CODIGO_P = /^P0*(\d+)$/i;

function numeroNoIntervalo(n, min, max) {
  return Number.isFinite(n) && n >= min && n <= max ? n : null;
}

function textoQr(q) {
  const codigo = String(q?.codigo ?? "").trim();
  const descricao = String(q?.descricao ?? "").trim();
  return `${codigo} ${descricao}`.trim();
}

/**
 * Identifica visitante (1–5) ou prestador (1–10) por id, codigo, descricao ou ordem.
 * @returns {{ tipo: 'visitante'|'prestador', numero: number } | null}
 */
export function classificarQrFixo(q) {
  const id = String(q?.id ?? "").trim();
  const codigo = String(q?.codigo ?? "").trim();
  const ordem = Number(q?.ordem);

  let m = id.match(RE_ID_VISITANTE);
  if (m) {
    const numero = numeroNoIntervalo(Number(m[1]), 1, LIMITE_VISITANTE);
    if (numero != null) return { tipo: TIPO_QR_VISITANTE, numero };
  }
  m = id.match(RE_ID_PRESTADOR);
  if (m) {
    const numero = numeroNoIntervalo(Number(m[1]), 1, LIMITE_PRESTADOR);
    if (numero != null) return { tipo: TIPO_QR_PRESTADOR, numero };
  }

  m = codigo.match(RE_CODIGO_V);
  if (m) {
    const numero = numeroNoIntervalo(Number(m[1]), 1, LIMITE_VISITANTE);
    if (numero != null) return { tipo: TIPO_QR_VISITANTE, numero };
  }
  m = codigo.match(RE_CODIGO_P);
  if (m) {
    const numero = numeroNoIntervalo(Number(m[1]), 1, LIMITE_PRESTADOR);
    if (numero != null) return { tipo: TIPO_QR_PRESTADOR, numero };
  }

  if (Number.isFinite(ordem)) {
    if (ordem === 0) {
      return { tipo: TIPO_QR_VISITANTE, numero: 1 };
    }
    if (ordem >= 1 && ordem <= LIMITE_VISITANTE) {
      return { tipo: TIPO_QR_VISITANTE, numero: ordem };
    }
    if (ordem >= 100 && ordem <= 100 + LIMITE_PRESTADOR) {
      return { tipo: TIPO_QR_PRESTADOR, numero: ordem - 100 };
    }
  }

  const t = textoQr(q);
  if (!t) return null;

  const mPrest = t.match(RE_PRESTADOR);
  if (mPrest) {
    const numero = numeroNoIntervalo(Number(mPrest[1]), 1, LIMITE_PRESTADOR);
    if (numero != null) return { tipo: TIPO_QR_PRESTADOR, numero };
  }

  const mVis = t.match(RE_VISITANTE);
  if (mVis) {
    const numero = numeroNoIntervalo(Number(mVis[1]), 1, LIMITE_VISITANTE);
    if (numero != null) return { tipo: TIPO_QR_VISITANTE, numero };
  }

  return null;
}

/**
 * @param {{ tipoPrestadorServico?: boolean, tipo_prestador_servico?: boolean } | null | undefined} flags
 * @returns {'visitante'|'prestador'}
 */
export function tipoQrDaVisita(flags) {
  const prestador = Boolean(flags?.tipoPrestadorServico ?? flags?.tipo_prestador_servico);
  return prestador ? TIPO_QR_PRESTADOR : TIPO_QR_VISITANTE;
}

/** Inclui cartões com id padrão (visitante-1 …) mesmo se classificação por texto falhar. */
function completarPoolPorIdsPadrao(arr, tipo) {
  const limite = tipo === TIPO_QR_PRESTADOR ? LIMITE_PRESTADOR : LIMITE_VISITANTE;
  const prefix = tipo === TIPO_QR_PRESTADOR ? "prestador" : "visitante";
  const porNumero = new Map();

  for (const q of arr) {
    const cls = classificarQrFixo(q);
    if (cls && cls.tipo === tipo && !porNumero.has(cls.numero)) {
      porNumero.set(cls.numero, q);
    }
  }

  for (let n = 1; n <= limite; n++) {
    if (porNumero.has(n)) continue;
    const id = `${prefix}-${n}`;
    const found = arr.find((q) => String(q?.id ?? "").toLowerCase() === id);
    if (found) porNumero.set(n, found);
  }

  return [...porNumero.entries()]
    .sort((a, b) => a[0] - b[0])
    .map(([, q]) => q);
}

/**
 * Cartões disponíveis para vínculo. O vínculo é liberado: qualquer cartão ativo
 * pode ser usado em qualquer tipo de visita (visitante ou prestador). Mantém a
 * deduplicação por tipo+número e a ordenação (visitantes 1…5 e depois prestadores 1…10).
 * O parâmetro `tipoPool` é ignorado (mantido por compatibilidade de assinatura).
 */
export function filtrarEOrdenarQrPool(lista) {
  const arr = Array.isArray(lista) ? lista : [];
  const porChave = new Map();
  arr.forEach((q) => {
    const cls = classificarQrFixo(q);
    if (!cls) return;
    const chave = `${cls.tipo}-${cls.numero}`;
    if (!porChave.has(chave)) {
      const ordem = (cls.tipo === TIPO_QR_PRESTADOR ? 100 : 0) + cls.numero;
      porChave.set(chave, { q, ordem });
    }
  });

  if (porChave.size > 0) {
    return [...porChave.values()].sort((a, b) => a.ordem - b.ordem).map((e) => e.q);
  }

  return [
    ...completarPoolPorIdsPadrao(arr, TIPO_QR_VISITANTE),
    ...completarPoolPorIdsPadrao(arr, TIPO_QR_PRESTADOR),
  ];
}

export function qrEstaDisponivelParaVisitante(q, visitaId, visitanteIndex) {
  if (!q?.vinculadoVisitaId) return true;
  const id = visitaId != null ? String(visitaId) : "";
  const idx = Number(visitanteIndex);
  return String(q.vinculadoVisitaId) === id && Number(q.vinculadoVisitanteIndex) === idx;
}

/**
 * QRs do pool para o select: todos do pool aparecem; os em uso em outra visita ficam desabilitados.
 */
export function qrOpcoesPoolParaSelect({ lista, visitaId, visitanteIndex, flagsVisita }) {
  const tipo = tipoQrDaVisita(flagsVisita);
  const pool = filtrarEOrdenarQrPool(lista, tipo);
  return pool.map((qr) => {
    const disponivel = qrEstaDisponivelParaVisitante(qr, visitaId, visitanteIndex);
    return {
      qr,
      disponivel,
      emUso: !disponivel && Boolean(qr?.vinculadoVisitaId),
    };
  });
}

/**
 * QRs livres ou já vinculados ao visitante desta tela (para validações).
 */
export function qrCodesDisponiveisParaVisita({ lista, visitaId, visitanteIndex, flagsVisita }) {
  return qrOpcoesPoolParaSelect({ lista, visitaId, visitanteIndex, flagsVisita })
    .filter((o) => o.disponivel)
    .map((o) => o.qr);
}

/** Quantos QRs existem no pool (sem filtro de ocupação). */
export function contarQrNoPool(lista, flagsVisita) {
  return filtrarEOrdenarQrPool(lista, tipoQrDaVisita(flagsVisita)).length;
}

/** Rótulo amigável para listas e selects (ex.: «Visitante 3 — V003»). */
export function rotuloQrFixo(q) {
  const codigo = String(q?.codigo ?? "").trim();
  const descricao = String(q?.descricao ?? "").trim();
  const cls = classificarQrFixo(q);
  if (cls) {
    const prefixo =
      cls.tipo === TIPO_QR_PRESTADOR
        ? `Prestador de serviço ${cls.numero}`
        : `Visitante ${cls.numero}`;
    if (codigo && descricao && descricao !== codigo) return `${prefixo} — ${codigo} (${descricao})`;
    if (codigo) return `${prefixo} — ${codigo}`;
    if (descricao) return `${prefixo} — ${descricao}`;
    return prefixo;
  }
  if (codigo && descricao) return `${codigo} — ${descricao}`;
  return codigo || descricao || "—";
}

/** Título da seção de seleção (vínculo liberado para qualquer tipo de visita). */
export function tituloPoolQr() {
  return "QR codes disponíveis";
}
